import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import useEvent from 'hooks/useEvent';
import { track } from 'apis/amethyst';
import { evPaginationClick } from 'events/account';
import { getAmethystPageType } from 'helpers/analytics';

import css from 'styles/components/account/pagination.scss';

const createPageGroups = (totalPages, queryParams) => {
  const { page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;
  const pageGroup1 = [];
  const pageGroup2 = [];
  const pageGroup3 = [];

  if (totalPages <= 3) {
    for (let i = 1; i <= totalPages; i++) {
      pageGroup1.push(i);
    }
  } else if (totalPages === 4) {
    if (currentPage <= 2) {
      pageGroup1.push(1, 2, 3);
      pageGroup2.push(4);
    } else {
      pageGroup1.push(1);
      pageGroup2.push(2, 3, 4);
    }
  } else {
    if (currentPage <= 2) {
      pageGroup1.push(1, 2, 3);
      pageGroup2.push(totalPages);
    } else if (currentPage >= totalPages - 1) {
      pageGroup1.push(1);
      pageGroup2.push(totalPages - 2, totalPages - 1, totalPages);
    } else {
      pageGroup1.push(1);
      pageGroup2.push(currentPage - 1, currentPage, currentPage + 1);
      pageGroup3.push(totalPages);
    }
  }

  return { pageGroup1, pageGroup2, pageGroup3 };
};

const makePreviousPage = (pathname, queryParams, testId) => {
  const { page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;

  if (currentPage !== 1) {
    const previousPageParams = queryString.stringify({
      ...queryParams,
      page: currentPage - 1
    });
    return (
      <Link
        to={`${pathname}?${previousPageParams}`}
        data-page-number={currentPage - 1}
        className={css.previousPage}
        data-test-id={testId('prevButton')}
      >
        Previous Page
      </Link>
    );
  }
};

const makeNextPage = (totalPages, pathname, queryParams, testId) => {
  const { page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;

  if (currentPage !== totalPages) {
    const nextPageParams = queryString.stringify({
      ...queryParams,
      page: currentPage + 1
    });
    return (
      <Link to={`${pathname}?${nextPageParams}`} data-page-number={currentPage + 1} className={css.nextPage} data-test-id={testId('nextButton')}>
        Next Page
      </Link>
    );
  }
};

const makePageGroup1 = (pageGroup, totalPages, pathname, queryParams, testId) => {
  const { page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;

  if (totalPages !== 1) {
    return (
      <span className={css.pageGroup}>
        {pageGroup.map(page => {
          if (page === currentPage) {
            return (
              <span key={page} className={css.currentPage} data-test-id={testId('currentPageDesktop')}>
                {page}
              </span>
            );
          } else {
            const pageGroupParams = queryString.stringify({
              ...queryParams,
              page
            });
            return (
              <Link key={page} data-page-number={page} to={`${pathname}?${pageGroupParams}`}>
                {page}
              </Link>
            );
          }
        })}
      </span>
    );
  }
};
const makePageGroup2And3 = (pageGroup, pathname, queryParams) => {
  const { page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;

  if (pageGroup.length) {
    return (
      <span className={css.pageGroup}>
        ...
        {pageGroup.map(page => {
          if (page === currentPage) {
            return (
              <span key={page} className={css.currentPage}>
                {page}
              </span>
            );
          } else {
            const pageGroupParams = queryString.stringify({
              ...queryParams,
              page
            });
            return (
              <Link key={page} data-page-number={page} to={`${pathname}?${pageGroupParams}`}>
                {page}
              </Link>
            );
          }
        })}
      </span>
    );
  }
};

export const Pagination = ({ pageSizeValues, location, totalPages, isOverview, onUpdatePageSize }, { testId }) => {
  const { pathname, search } = location;
  const queryParams = queryString.parse(search);
  const pageType = useSelector(state => state.pageView?.pageType);
  const pageGroups = useRef(null);

  // Append `overview` query param for desktop pagination
  // Esentially it's a workaround for ignoring scroll to top on query param change
  // for Order History Overview component (hence the name)
  // See routes.js for implementation details
  if (isOverview) {
    queryParams.overview = true;
  }

  const { pageSize = '10', page } = queryParams;
  const currentPage = parseInt(page, 10) || 1;
  const { pageGroup1, pageGroup2, pageGroup3 } = createPageGroups(totalPages, queryParams);

  const handlePaginationClickTrack = e => {
    const { pageNumber } = e.target.dataset;
    if (pageNumber) {
      track(() => [
        evPaginationClick,
        {
          currentPage,
          nextPage: parseInt(pageNumber),
          sourcePage: getAmethystPageType(pageType)
        }
      ]);
    }
  };

  useEvent(pageGroups.current, 'click', handlePaginationClickTrack);

  return (
    <div className={css.pagination}>
      <div className={css.pageSize}>
        <label className="body-s" htmlFor="pageSize">
          Orders per page:
        </label>
        <select id="pageSize" data-test-id={testId('pageSize')} value={parseInt(pageSize)} onChange={onUpdatePageSize}>
          {pageSizeValues.map((size, index) => (
            <option key={index} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
      <div ref={pageGroups} className={css.pageGroups}>
        {makePreviousPage(pathname, queryParams, testId)}
        {makePageGroup1(pageGroup1, totalPages, pathname, queryParams, testId)}
        {makePageGroup2And3(pageGroup2, pathname, queryParams)}
        {makePageGroup2And3(pageGroup3, pathname, queryParams)}
        {makeNextPage(totalPages, pathname, queryParams, testId)}
      </div>
    </div>
  );
};

Pagination.contextTypes = {
  testId: PropTypes.func
};

Pagination.propTypes = {
  pageSizeValues: PropTypes.arrayOf(PropTypes.number).isRequired,
  location: PropTypes.object.isRequired,
  totalPages: PropTypes.number.isRequired,
  isOverview: PropTypes.bool,
  onUpdatePageSize: PropTypes.func.isRequired
};

export default Pagination;
